import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { FeedbackType } from '../shared/feedbackHelpers';
import { ScrollBar } from '../ScrollBar/ScrollBar';
import { ModalFooter, ModalHeader } from './shared/ModalParts';
import { ModalScene } from './shared/ModalScene';
import './Modal.css';

/** Standard modal dialog with header, scrollable body slot, and footer actions. */
export type ModalProps = {
  title?: ReactNode;
  subtitle?: string;
  /** Optional feedback icon variant (success, warning, error, info) shown in the header. */
  feedbackType?: FeedbackType;
  showSubtitle?: boolean;
  showClose?: boolean;
  showScrollBar?: boolean;
  showFooter?: boolean;
  showFooterSlot?: boolean;
  showTertiaryButton?: boolean;
  showMainButton?: boolean;
  primaryLabel?: string;
  /** Optional leading icon for the primary footer button. */
  primaryIcon?: ReactNode;
  /** When true, the primary footer button is disabled. */
  primaryDisabled?: boolean;
  /** Disable the primary footer button while a `ReadOnlyProvider` is read-only, with its reason as a tooltip. */
  primaryDisableWhenReadOnly?: boolean;
  /** Leading footer content (left of Cancel / Done). Shown when `showFooterSlot` is true. */
  footerSlot?: ReactNode;
  tertiaryLabel?: string;
  /** Optional leading icon for the tertiary footer button. */
  tertiaryIcon?: ReactNode;
  /** When true, the tertiary footer button is disabled. */
  tertiaryDisabled?: boolean;
  showOverlay?: boolean;
  footerType?: 'default' | 'checkbox';
  danger?: boolean;
  /** When true the body uses fixed min/max height and scrolls on overflow. When false the body grows with its content. */
  fixedHeight?: boolean;
  /**
   * Changing this value resets the body scroll position to the top.
   * Use when swapping steps inside a long-lived modal so the new step is not
   * shown mid-scroll from the previous one.
   */
  scrollResetKey?: string | number;
  bodyVariant?: 'slot' | 'text';
  /** Controls visibility. When omitted the modal manages its own open/close state. */
  open?: boolean;
  children?: ReactNode;
  className?: string;
  onCloseClick?: () => void;
  onPrimaryClick?: () => void;
  onTertiaryClick?: () => void;
};

export function Modal({
  title = 'Long title',
  subtitle = 'Subtitle',
  feedbackType,
  showSubtitle = true,
  showClose = true,
  showScrollBar = true,
  showFooter = true,
  showFooterSlot = false,
  showTertiaryButton = true,
  showMainButton = true,
  primaryLabel,
  primaryIcon,
  primaryDisabled = false,
  primaryDisableWhenReadOnly = false,
  footerSlot,
  tertiaryLabel,
  tertiaryIcon,
  tertiaryDisabled = false,
  showOverlay = true,
  footerType = 'default',
  danger = false,
  fixedHeight = true,
  scrollResetKey,
  bodyVariant = 'slot',
  open: openProp,
  children,
  className = '',
  onCloseClick,
  onPrimaryClick,
  onTertiaryClick,
}: ModalProps) {
  const [internalOpen, setInternalOpen] = useState(true);
  const isControlled = openProp !== undefined;
  const visible = isControlled ? openProp : internalOpen;

  // X / overlay dismiss — always onCloseClick (not tertiary). Callers that
  // want the same action for both should pass the same handler to both props.
  const handleClose = useCallback(() => {
    if (!isControlled) setInternalOpen(false);
    onCloseClick?.();
  }, [isControlled, onCloseClick]);

  const bodyRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);

  const measureBodyOverflow = useCallback(() => {
    const el = bodyRef.current;
    if (!el) {
      setCanScroll(false);
      return;
    }
    setCanScroll(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useLayoutEffect(() => {
    if (scrollResetKey === undefined) return;
    const el = bodyRef.current;
    if (el) el.scrollTop = 0;
  }, [scrollResetKey]);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    measureBodyOverflow();
    const observer = new ResizeObserver(measureBodyOverflow);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [measureBodyOverflow, scrollResetKey, fixedHeight, visible]);

  if (!visible) return null;

  const dialog = (
    <div
      className={['spark-modal', 'spark-modal-slot', className].filter(Boolean).join(' ')}
      role="dialog"
      aria-modal="true"
      aria-labelledby="spark-modal-title"
    >
      <ModalHeader
        title={title}
        subtitle={subtitle}
        showSubtitle={showSubtitle}
        showClose={showClose}
        feedbackType={feedbackType}
        onCloseClick={handleClose}
      />
      {(children || (fixedHeight && bodyVariant === 'slot')) ? (
        <div className="spark-modal-body-shell">
          <div
            className={
              bodyVariant === 'text'
                ? 'spark-modal-body spark-modal-body-text'
                : [
                    'spark-modal-body',
                    'spark-modal-body-slot',
                    !fixedHeight && 'spark-modal-body-slot--dynamic',
                    canScroll && 'spark-modal-body-slot--scrollable',
                  ]
                    .filter(Boolean)
                    .join(' ')
            }
            ref={bodyRef}
          >
            {children}
          </div>
          {showScrollBar ? <ScrollBar containerRef={bodyRef} className="spark-modal-scrollbar" /> : null}
        </div>
      ) : null}
      {showFooter ? (
        <ModalFooter
          footerType={footerType}
          danger={danger}
          showFooterSlot={showFooterSlot}
          showTertiaryButton={showTertiaryButton}
          showMainButton={showMainButton}
          primaryLabel={primaryLabel}
          primaryIcon={primaryIcon}
          primaryDisabled={primaryDisabled}
          primaryDisableWhenReadOnly={primaryDisableWhenReadOnly}
          footerSlot={footerSlot}
          tertiaryLabel={tertiaryLabel}
          tertiaryIcon={tertiaryIcon}
          tertiaryDisabled={tertiaryDisabled}
          onPrimaryClick={onPrimaryClick}
          onTertiaryClick={onTertiaryClick}
        />
      ) : null}
    </div>
  );

  return (
    <ModalScene showOverlay={showOverlay} size="slot" onOverlayClick={handleClose}>
      {dialog}
    </ModalScene>
  );
}
